import { Stack, Typography } from "@mui/material";
import {
  LoadingIndicator,
  useSpacing,
  useTypography,
} from "@surya-digital/leo-reactjs-material-ui";
import React from "react";
import { DividerComponent } from "../../../surveys/components/commonQuestionConfigurationDetailsComponents/DividerComponent";
import { useTranslation } from "react-i18next";
import { observer } from "mobx-react";
import {
  useEmailCycleDetailsStore,
  useUpdateEmailCycleStore,
} from "../../stores/Hooks";
import {
  RecurringScheduleEmailForm,
  RecurringScheduleEmailFormReadOnly,
} from "../forms/RecurringScheduleEmailForm";
import { SingleTimeScheduleEmailForm } from "../forms/SingleTimeScheduleEmailForm";
import { ImmediateScheduleEmailForm } from "../forms/ImmediateScheduleEmailForm";
import { EmailCycleBasicInformationForm } from "../forms/EmailCycleBasicInformationForm";
import { EmailCycleMessageCustomizationForm } from "../forms/EmailCycleMessageCustomizationForm";
import { EmailCycleStatus } from "@pulse/pulse-rpcs/lib/email-cycle/emailCycleStatus";
import { useSignInStore } from "../../../auth/store/hook";
import { UserPrivileges } from "../../../store/user/UserPrivileges";

const SCHEDULE_FORM_MAX_WIDTH = "600px";

export const EmailCycleDetailsTab: React.FC = observer(() => {
  const { t } = useTranslation();
  const spacing = useSpacing();
  const typography = useTypography();

  const signInStore = useSignInStore();
  const updateEmailCycleStore = useUpdateEmailCycleStore();
  const emailCycleDetailsStore = useEmailCycleDetailsStore();

  const hasPrivilege = signInStore.isPrivilegeGranted(
    UserPrivileges.processEmailCycle,
  );

  const isQueued =
    emailCycleDetailsStore.status === EmailCycleStatus.EmailCycleStatus.QUEUED;

  const isScheduleInProgress =
    emailCycleDetailsStore.status ===
    EmailCycleStatus.EmailCycleStatus.IN_PROGRESS;

  const isRecurring =
    updateEmailCycleStore.scheduleType.type === "RECURRING_SCHEDULE";
  const isImmediate =
    updateEmailCycleStore.scheduleType.type === "IMMEDIATE_SCHEDULE";

  const readOnly =
    !hasPrivilege ||
    isImmediate ||
    !(isRecurring ? isQueued || isScheduleInProgress : isQueued);

  const isCycleInProgress = !emailCycleDetailsStore.isScheduleEditable;

  const recurringScheduleEmailFormReadOnly: RecurringScheduleEmailFormReadOnly =
    {
      startDate: isCycleInProgress || readOnly,
      endDate: readOnly,
      time: readOnly,
      daysOfWeek: isQueued || isCycleInProgress || readOnly,
    };

  return (
    <Stack padding={spacing.space2XL} height="100%" width="100%">
      <LoadingIndicator
        variant="overlay"
        isLoading={updateEmailCycleStore.loadingUpdateEmailCycle}
      />
      <Stack
        gap={spacing.space2XL}
        divider={<DividerComponent orientation="horizontal" width="100%" />}
      >
        <Stack gap={spacing.spaceXL} maxWidth={SCHEDULE_FORM_MAX_WIDTH}>
          <Typography {...typography.sh2}>
            {updateEmailCycleStore.scheduleType.type === "RECURRING_SCHEDULE"
              ? t("emailCycles.recurringScheduleInformation")
              : t("emailCycles.scheduleInformation")}
          </Typography>

          {updateEmailCycleStore.scheduleType.type === "RECURRING_SCHEDULE" ? (
            <RecurringScheduleEmailForm
              startDate={updateEmailCycleStore.scheduleType.startDate}
              endDate={updateEmailCycleStore.scheduleType.endDate}
              time={updateEmailCycleStore.scheduleType.timeOfDay}
              daysOfWeek={updateEmailCycleStore.scheduleType.daysOfWeek}
              readonly={recurringScheduleEmailFormReadOnly}
            />
          ) : updateEmailCycleStore.scheduleType.type ===
            "IMMEDIATE_SCHEDULE" ? (
            <ImmediateScheduleEmailForm />
          ) : (
            <SingleTimeScheduleEmailForm
              date={updateEmailCycleStore.scheduleType.date}
              time={updateEmailCycleStore.scheduleType.time}
              readOnly={readOnly}
            />
          )}
        </Stack>

        <EmailCycleBasicInformationForm
          cycleName={updateEmailCycleStore.cycleName}
          displayCycleName={emailCycleDetailsStore.displayCycleName.name}
          fromEmailId={updateEmailCycleStore.senderEmail}
          fromEmailIdApplicationErrorMessage={
            updateEmailCycleStore.senderEmailRPCError
              ? t(
                  "emailCycles.fromEmailDomainNotAllowedErrorMessage",
                  "The sender email domain is not allowed",
                )
              : undefined
          }
          onFromEmailIdChange={updateEmailCycleStore.setSenderEmail}
          displayName={updateEmailCycleStore.senderName}
          subject={updateEmailCycleStore.emailContent.subject}
          readOnly={readOnly}
        />

        <EmailCycleMessageCustomizationForm
          templateId={updateEmailCycleStore.templateId}
          setTemplateId={updateEmailCycleStore.setTemplateId}
          templateName={updateEmailCycleStore.templateName}
          templateContent={updateEmailCycleStore.emailContent.body}
          readOnly={readOnly}
        />
      </Stack>
    </Stack>
  );
});
